import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import dayjs from 'dayjs'
import { Loader2, Search, Unplug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/misc'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { cn, initials } from '@/lib/utils'
import { statusMeta, useDeleteZaloAccount, type ChannelAccount } from '@/hooks/use-integrations'
import { useMyPermissions } from '@/hooks/use-settings'

// ─────────────────────────────────────────────────────────────────────────────
// Bảng có chọn nhiều — khung chung cho mọi trang kênh
// ─────────────────────────────────────────────────────────────────────────────

export interface TableColumn<T> {
  key: string
  header: ReactNode
  cell: (row: T) => ReactNode
  className?: string
  /** Ghim cột bên phải khi bảng phải cuộn ngang (cột Thao tác). */
  sticky?: boolean
}

export interface RowFilter<T> {
  value: string
  label: string
  match: (row: T) => boolean
}

/**
 * Bảng kiểu SaleSmartly: ô tìm kiếm + lọc trạng thái, cột checkbox, thanh
 * "Đã chọn X trong số N" ở chân bảng kèm thao tác hàng loạt.
 */
export function SelectableTable<T>({
  rows,
  rowKey,
  columns,
  loading,
  error,
  empty,
  searchPlaceholder,
  searchText,
  filters,
  toolbarExtra,
  renderDetail,
  bulkActions,
  itemLabel,
}: {
  rows: T[] | undefined
  rowKey: (row: T) => string
  columns: TableColumn<T>[]
  loading?: boolean
  error?: boolean
  /** Hiện thay cho bảng khi chưa có bản ghi nào. */
  empty: ReactNode
  searchPlaceholder?: string
  searchText: (row: T) => string
  filters?: RowFilter<T>[]
  toolbarExtra?: ReactNode
  /** Hàng phụ tràn hết bề ngang ngay dưới hàng chính (cảnh báo, hạn mức, chẩn đoán…). */
  renderDetail?: (row: T) => ReactNode
  bulkActions?: (selected: T[], clear: () => void) => ReactNode
  itemLabel?: string
}) {
  const { t } = useTranslation('chanZalo')
  const placeholder = searchPlaceholder ?? t('table.searchPlaceholder')
  const item = itemLabel ?? t('table.itemLabel')
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState('all')
  const [picked, setPicked] = useState<Set<string>>(new Set())

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const f = filters?.find((x) => x.value === filter)
    return (rows ?? []).filter(
      (r) => (!f || f.match(r)) && (!needle || searchText(r).toLowerCase().includes(needle)),
    )
  }, [rows, q, filter, filters, searchText])

  // Chỉ tính các dòng đang hiển thị — bỏ lọc thì dòng bị ẩn không "chọn ngầm".
  const selected = shown.filter((r) => picked.has(rowKey(r)))
  const allOn = shown.length > 0 && selected.length === shown.length
  const clear = () => setPicked(new Set())
  const toggleAll = () => setPicked(allOn ? new Set() : new Set(shown.map(rowKey)))
  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  if (loading) return <Loading label={t('table.loading')} />
  if (error) return <ErrorState />
  if (!rows?.length) return <>{empty}</>

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="bg-secondary pr-8 hover:bg-card focus-visible:bg-card"
          />
          <Search className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>
        {filters && (
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('table.allStatuses')}</SelectItem>
              {filters.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {toolbarExtra && <div className="ml-auto flex items-center gap-2">{toolbarExtra}</div>}
      </div>

      <div className="overflow-hidden rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="w-12 px-4 py-3 text-left">
                  <Checkbox
                    checked={allOn ? true : selected.length ? 'indeterminate' : false}
                    onCheckedChange={toggleAll}
                    aria-label={t('common:actions.selectAll')}
                  />
                </th>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={cn(
                      'whitespace-nowrap px-4 py-3 text-left font-medium',
                      c.sticky && STICKY_CELL + ' bg-muted',
                      c.className,
                    )}
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-4 py-10 text-center text-muted-foreground">
                    {t('table.noMatch', { item })}
                  </td>
                </tr>
              )}
              {shown.map((row) => {
                const id = rowKey(row)
                const detail = renderDetail?.(row)
                const on = picked.has(id)
                return (
                  <Fragment key={id}>
                    <tr className={cn('group border-t transition-colors hover:bg-muted/50', on && 'bg-accent/40')}>
                      <td className="px-4 py-3.5 align-middle">
                        <Checkbox checked={on} onCheckedChange={() => toggle(id)} aria-label={t('table.selectRow')} />
                      </td>
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          className={cn(
                            'px-4 py-3.5 align-middle',
                            c.sticky && STICKY_CELL + ' bg-card group-hover:bg-muted',
                            c.className,
                          )}
                        >
                          {c.cell(row)}
                        </td>
                      ))}
                    </tr>
                    {detail && (
                      <tr className={cn(on && 'bg-accent/40')}>
                        <td />
                        <td colSpan={columns.length} className="px-4 pb-3.5 pt-0">
                          {/* w-0 min-w-full: hàng phụ theo bề ngang bảng, không tự kéo giãn
                              bảng (dòng hạn mức dài từng đẩy cột Thao tác ra ngoài khung). */}
                          <div className="w-0 min-w-full">{detail}</div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t px-4 py-3 text-sm">
          <span className="text-muted-foreground">
            {t('table.selectedOf', { selected: selected.length, total: shown.length, item })}
          </span>
          <button
            type="button"
            onClick={allOn ? clear : toggleAll}
            className="text-primary hover:underline"
            disabled={!shown.length}
          >
            {allOn ? t('table.deselect') : t('common:actions.selectAll')}
          </button>
          {selected.length > 0 && bulkActions && (
            <div className="ml-auto flex items-center gap-2">{bulkActions(selected, clear)}</div>
          )}
        </div>
      </div>
    </div>
  )
}

// 20/10/2026: chỉ ghim cột Thao tác TỪ md trở lên — dưới 768px cột ghim rộng hơn cả khung nhìn (293px) che kín
// mọi cột khác (Tài khoản/Trạng thái…) dù cuộn kiểu gì cũng không thấy được; điện thoại cuộn ngang bình thường
// như DataTable, không ghim, vẫn thấy hết dữ liệu.
const STICKY_CELL = 'md:sticky md:right-0 md:z-[1] md:shadow-[-6px_0_8px_-6px_hsl(var(--foreground)/0.12)]'

// ─────────────────────────────────────────────────────────────────────────────
// Phần riêng cho tài khoản kênh (`/zalo-accounts`)
// ─────────────────────────────────────────────────────────────────────────────

export function AccountCell({
  acc,
  fallbackName,
  sub,
}: {
  acc: ChannelAccount
  fallbackName: string
  sub?: ReactNode
}) {
  const name = acc.displayName || acc.phone || fallbackName
  return (
    <div className="flex min-w-[200px] max-w-[280px] items-center gap-3">
      <Avatar className="h-10 w-10">
        {acc.avatarUrl && <AvatarImage src={acc.avatarUrl} alt={name} />}
        <AvatarFallback className="bg-primary/10 text-primary">{initials(name)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="truncate font-medium" title={name}>
          {name}
        </p>
        {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  )
}

/** Cột chung: Tài khoản · Trạng thái · Người phụ trách · Kết nối lần cuối (+ Thao tác). */
export function accountColumns({
  fallbackName,
  sub,
  actions,
}: {
  fallbackName: string
  sub?: (acc: ChannelAccount) => ReactNode
  actions?: (acc: ChannelAccount) => ReactNode
}): TableColumn<ChannelAccount>[] {
  const cols: TableColumn<ChannelAccount>[] = [
    {
      key: 'account',
      header: i18n.t('chanZalo:table.colAccount'),
      cell: (a) => <AccountCell acc={a} fallbackName={fallbackName} sub={sub?.(a)} />,
    },
    {
      key: 'status',
      header: i18n.t('chanZalo:table.colStatus'),
      cell: (a) => {
        if (a.lockedByPlatform) return <Badge variant="destructive">{i18n.t('chanZalo:table.lockedByPlatform')}</Badge>
        const s = statusMeta(a.liveStatus, a.isDisabled)
        return <Badge variant={s.variant}>{s.label}</Badge>
      },
    },
    {
      key: 'owner',
      header: i18n.t('chanZalo:table.colOwner'),
      cell: (a) =>
        a.owner ? (
          <span className="block max-w-[180px] truncate" title={a.owner.email}>
            {a.owner.fullName}
          </span>
        ) : (
          <span className="text-muted-foreground">--</span>
        ),
    },
    {
      key: 'last',
      header: i18n.t('chanZalo:table.colLastConnected'),
      className: 'whitespace-nowrap',
      cell: (a) =>
        a.lastConnectedAt ? (
          dayjs(a.lastConnectedAt).format('DD/MM/YYYY HH:mm')
        ) : (
          <span className="text-muted-foreground">{i18n.t('chanZalo:table.neverConnected')}</span>
        ),
    },
  ]
  if (actions) {
    cols.push({
      key: 'actions',
      header: i18n.t('chanZalo:table.colActions'),
      className: 'w-px whitespace-nowrap text-right',
      sticky: true,
      cell: (a) => <div className="flex items-center justify-end gap-1">{actions(a)}</div>,
    })
  }
  return cols
}

const isLive = (a: ChannelAccount) => a.liveStatus === 'connected' && !a.isDisabled

export const ACCOUNT_FILTERS: RowFilter<ChannelAccount>[] = [
  // Getter: nhãn đọc i18n lúc dùng để đổi ngôn ngữ có hiệu lực.
  { value: 'connected', get label() { return i18n.t('chanZalo:table.filterConnected') }, match: isLive },
  { value: 'attention', get label() { return i18n.t('chanZalo:table.filterAttention') }, match: (a) => !a.isDisabled && !isLive(a) },
  { value: 'disabled', get label() { return i18n.t('chanZalo:table.filterDisabled') }, match: (a) => a.isDisabled },
]

export function accountSearchText(a: ChannelAccount): string {
  return [a.displayName, a.phone, a.externalUid, a.externalPageId, a.owner?.fullName, a.owner?.email]
    .filter(Boolean)
    .join(' ')
}

/** Ngắt kết nối nhiều tài khoản một lượt (có hỏi lại). Gọi tuần tự để lỗi rõ từng cái. */
export function BulkDisconnectButton({
  selected,
  onDone,
  description,
}: {
  selected: ChannelAccount[]
  onDone: () => void
  description?: string
}) {
  const { t } = useTranslation('chanZalo')
  const remove = useDeleteZaloAccount()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  // Ngắt kết nối = DELETE /zalo-accounts/:id → cần `integrations.delete`; thiếu quyền thì không hiện nút.
  const canDelete = !!useMyPermissions().data?.has('integrations.delete')

  const run = async () => {
    setBusy(true)
    let ok = 0
    const errors: string[] = []
    for (const a of selected) {
      try {
        await remove.mutateAsync(a.id)
        ok++
      } catch (e) {
        errors.push(`${a.displayName || a.id}: ${apiError(e)}`)
      }
    }
    setBusy(false)
    setOpen(false)
    if (ok) toast.success(t('table.bulkDisconnected', { count: ok }))
    if (errors.length) toast.error(errors.join('\n'))
    onDone()
  }

  if (!canDelete) return null

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Unplug /> {t('common:actions.disconnect')} ({selected.length})
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('table.bulkTitle', { count: selected.length })}</DialogTitle>
            <DialogDescription>{description ?? t('table.bulkDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              {t('personal.cancel')}
            </Button>
            <Button variant="destructive" onClick={run} disabled={busy}>
              {busy && <Loader2 className="animate-spin" />}
              {t('common:actions.disconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
